import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { LOGO_URL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Map, PlusCircle, User, LogOut, Shield, Tag, Menu, Home, Heart, Star, BookOpen, MessageSquare } from "lucide-react";
import { fileUrl } from "@/lib/api";

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const { refreshUser } = useAuth();
  const fpNotifiedRef = useRef(false);

  useEffect(() => {
    const fp = user?.free_pass;
    if (fp && fp.active && !fp.notified && !fpNotifiedRef.current) {
      fpNotifiedRef.current = true;
      const when = fp.expires ? `until ${new Date(fp.expires).toLocaleDateString()}` : "forever";
      toast.success(`🎁 An admin gifted you a FREE pass — full access ${when}, no card needed. Enjoy Yard-skip!`, { duration: 9000 });
      api.post("/me/free-pass/ack").then(() => refreshUser && refreshUser()).catch(() => {});
    }
  }, [user, refreshUser]);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let active = true;
    const poll = () => api.get("/conversations/unread-count").then(({ data }) => { if (active) setUnread(data.count || 0); }).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { active = false; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [user, location.pathname]);

  const nav = [
    { to: "/browse", label: "Browse Map", icon: Map, testid: "nav-browse" },
    { to: "/pricing", label: "Plans", icon: Tag, testid: "nav-pricing" },
    { to: "/guide", label: "User Guide", icon: BookOpen, testid: "nav-guide" },
  ];

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  const roleBadge = (() => {
    if (!user) return null;
    if (user.role === "admin" || (user.permissions && user.permissions.length > 0))
      return { label: "Admin", cls: "bg-slate-900 text-white", Icon: Shield };
    if (user.plan?.type === "seller_pro_monthly")
      return { label: "Pro Seller", cls: "bg-ys-gold/20 text-amber-700 border border-amber-300", Icon: Star };
    if (user.role === "seller")
      return { label: "Seller", cls: "bg-sky-100 text-ys-sky", Icon: Tag };
    return { label: "Buyer", cls: "bg-emerald-100 text-emerald-700", Icon: Map };
  })();

  const freePass = (() => {
    const fp = user?.free_pass;
    if (!fp || !fp.active) return null;
    if (!fp.expires) return { text: "Free pass — forever", title: "You have a free pass with full access, forever" };
    const days = Math.ceil((Date.parse(fp.expires) - Date.now()) / 86400000);
    if (days < 0) return null;
    return { text: days === 0 ? "Free pass — ends today" : `Free pass — ${days}d left`, title: `Your free pass expires ${new Date(fp.expires).toLocaleDateString()}` };
  })();

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
          <img src={LOGO_URL} alt="Yard-skip" className="h-10 w-auto rounded-md" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((n) => (
            <Link key={n.to} to={n.to} data-testid={n.testid}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                location.pathname === n.to ? "bg-sky-100 text-ys-sky" : "text-slate-600 hover:bg-slate-100"
              }`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {(user?.role === "seller" || user?.role === "admin") && (
            <Button onClick={() => navigate("/create")} data-testid="header-create-btn"
              className="rounded-full bg-ys-sky hover:bg-sky-700 gap-1.5 hidden sm:flex">
              <PlusCircle className="w-4 h-4" /> Post a Sale
            </Button>
          )}
          {user ? (
            <>
              {roleBadge && (
                <span data-testid="role-badge" title={`Signed in as ${roleBadge.label}`}
                  className={`hidden sm:inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-bold uppercase tracking-wide ${roleBadge.cls}`}>
                  <roleBadge.Icon className="w-3.5 h-3.5" /> {roleBadge.label}
                </span>
              )}
              {freePass && (
                <span data-testid="free-pass-badge" title={freePass.title}
                  className="hidden sm:inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-bold bg-ys-gold/20 text-amber-700 border border-amber-300">
                  🎁 {freePass.text}
                </span>
              )}
            <button onClick={() => navigate("/messages")} data-testid="header-messages-btn" aria-label="Messages"
              className="relative p-2 rounded-full hover:bg-slate-100 transition-colors">
              <MessageSquare className="w-5 h-5 text-slate-600" />
              {unread > 0 && <span data-testid="messages-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-ys-sky text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="rounded-full outline-none">
                  <Avatar className="w-9 h-9 border-2 border-sky-200">
                    {user.picture ? <AvatarImage src={fileUrl(user.picture)} /> : null}
                    <AvatarFallback className="bg-ys-sky text-white text-sm font-bold">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-semibold truncate">{user.name || "Member"}</p>
                  <p className="text-xs text-slate-500 truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/browse")} data-testid="menu-browse"><Map className="w-4 h-4 mr-2" />Browse Map</DropdownMenuItem>
                {(user.role === "seller" || user.role === "admin") && (
                  <DropdownMenuItem onClick={() => navigate("/create")} data-testid="menu-create"><PlusCircle className="w-4 h-4 mr-2" />Post a Sale</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/messages")} data-testid="menu-messages"><MessageSquare className="w-4 h-4 mr-2" />Messages{unread > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-ys-sky text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile?tab=settings")} data-testid="menu-profile"><User className="w-4 h-4 mr-2" />My Profile & Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile?tab=saved")} data-testid="menu-saved"><Heart className="w-4 h-4 mr-2" />Saved sales</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile?tab=reviews")} data-testid="menu-reviews"><Star className="w-4 h-4 mr-2" />My reviews</DropdownMenuItem>
                {(user.role === "admin" || (user.permissions && user.permissions.length > 0)) && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="w-4 h-4 mr-2" />Admin Panel</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout" className="text-red-600"><LogOut className="w-4 h-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="header-login-btn" className="rounded-full">Sign in</Button>
              <Button onClick={() => navigate("/register")} data-testid="header-signup-btn" className="rounded-full bg-ys-sky hover:bg-sky-700">Sign up</Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="md:hidden">
              <button data-testid="mobile-menu-trigger" className="p-2 rounded-lg hover:bg-slate-100"><Menu className="w-5 h-5" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate("/")}><Home className="w-4 h-4 mr-2" />Home</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/browse")}><Map className="w-4 h-4 mr-2" />Browse Map</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/pricing")}><Tag className="w-4 h-4 mr-2" />Plans</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/guide")} data-testid="mobile-nav-guide"><BookOpen className="w-4 h-4 mr-2" />User Guide</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
